import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Users,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Trash2,
  UserCog,
  Shield,
  Ban,
  CheckCircle
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from '../../hooks/use-toast';

const roleColors = {
  buyer: 'bg-blue-900 text-blue-300',
  seller: 'bg-green-900 text-green-300',
  advertiser: 'bg-purple-900 text-purple-300',
  admin: 'bg-orange-900 text-orange-300',
};

const roleLabels = {
  buyer: 'Comprador',
  seller: 'Vendedor',
  advertiser: 'Anunciante',
  admin: 'Admin',
};

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [editingUser, setEditingUser] = useState(null);
  const limit = 20;

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const [usersData, countData] = await Promise.all([
        adminAPI.getUsers({ skip: page * limit, limit, role: roleFilter, search }),
        adminAPI.getUsersCount({ role: roleFilter, search })
      ]);
      setUsers(usersData);
      setTotalCount(countData.count);
    } catch (err) {
      console.error('Error loading users:', err);
      toast({ title: 'Error', description: 'Error al cargar usuarios', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [page, roleFilter, search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await adminAPI.updateUserRole(userId, newRole);
      loadUsers();
      setEditingUser(null);
      toast({ title: 'Éxito', description: 'Rol del usuario actualizado' });
    } catch (err) {
      toast({ title: 'Error', description: 'Error al actualizar rol', variant: 'destructive' });
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    
    try {
      await adminAPI.deleteUser(userId);
      loadUsers();
      toast({ title: 'Éxito', description: 'Usuario eliminado' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error al eliminar usuario', variant: 'destructive' });
    }
  };

  const handleToggleSuspend = async (userId) => {
    try {
      const result = await adminAPI.toggleSuspendUser(userId);
      toast({ title: result.is_suspended ? 'Usuario suspendido' : 'Usuario reactivado' });
      loadUsers();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error al suspender', variant: 'destructive' });
    }
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Usuarios</h1>
            <p className="text-gray-400 mt-1">{totalCount} usuarios registrados</p>
          </div>
          <Button onClick={loadUsers} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-gray-800 border-gray-700 p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  placeholder="Buscar por nombre o email..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  className="pl-10 bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant={roleFilter === null ? 'default' : 'outline'}
                onClick={() => { setRoleFilter(null); setPage(0); }}
                className={roleFilter === null ? 'bg-orange-500 hover:bg-orange-600' : 'border-gray-600 text-gray-300'}
                size="sm"
              >
                Todos
              </Button>
              {Object.entries(roleLabels).map(([key, label]) => (
                <Button
                  key={key}
                  variant={roleFilter === key ? 'default' : 'outline'}
                  onClick={() => { setRoleFilter(key); setPage(0); }}
                  className={roleFilter === key ? 'bg-orange-500 hover:bg-orange-600' : 'border-gray-600 text-gray-300'}
                  size="sm"
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </Card>

        {/* Users table */}
        <Card className="bg-gray-800 border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No se encontraron usuarios</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Usuario</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Rol</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Reputación</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Ventas</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Saldo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Plan</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Estado</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {users.map((user) => (
                    <tr key={user._id} className="hover:bg-gray-700/50">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={user.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}`}
                            alt={user.name}
                            className="w-10 h-10 rounded-full bg-gray-700"
                          />
                          <div>
                            <p className="text-white font-medium">{user.name}</p>
                            <p className="text-gray-400 text-sm">ID: {user._id?.slice(-8)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-gray-300">{user.email}</td>
                      <td className="px-4 py-4">
                        {editingUser === user._id ? (
                          <select
                            defaultValue={user.role}
                            onChange={(e) => handleRoleChange(user._id, e.target.value)}
                            onBlur={() => setEditingUser(null)}
                            autoFocus
                            className="px-2 py-1 rounded text-xs font-medium bg-gray-700 border-gray-600 text-white"
                          >
                            {Object.entries(roleLabels).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        ) : (
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium cursor-pointer ${roleColors[user.role]}`}
                            onClick={() => setEditingUser(user._id)}
                            title="Click para cambiar rol"
                          >
                            {roleLabels[user.role] || user.role}
                            {user.role === 'admin' && <Shield className="w-3 h-3 inline ml-1" />}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-white">
                        <span className="text-yellow-400">★</span> {user.reputation?.toFixed(1) || '5.0'}
                      </td>
                      <td className="px-4 py-4 text-white">{user.sales || 0}</td>
                      <td className="px-4 py-4 text-right">
                        <span className="text-green-400 font-mono text-sm">${(user.wallet_balance || 0).toLocaleString('es-MX', {minimumFractionDigits: 2})}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="px-2 py-1 rounded text-xs font-medium bg-blue-900/60 text-blue-300 capitalize">
                          {user.subscription_plan || 'gratis'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {user.is_suspended ? (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-red-900 text-red-300">Suspendido</span>
                        ) : (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-green-900 text-green-300">Activo</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingUser(user._id)}
                            className="text-gray-400 hover:text-white"
                            title="Cambiar rol"
                          >
                            <UserCog className="w-4 h-4" />
                          </Button>
                          {user.role !== 'admin' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleSuspend(user._id)}
                                className={user.is_suspended ? "text-green-400 hover:text-green-300" : "text-yellow-400 hover:text-yellow-300"}
                                title={user.is_suspended ? 'Reactivar' : 'Suspender'}
                                data-testid={`suspend-btn-${user._id}`}
                              >
                                {user.is_suspended ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(user._id)}
                                className="text-gray-400 hover:text-red-400"
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
              <p className="text-sm text-gray-400">
                Mostrando {page * limit + 1} - {Math.min((page + 1) * limit, totalCount)} de {totalCount}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="border-gray-600 text-gray-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                  className="border-gray-600 text-gray-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
